import React, { useState } from 'react';
import '../Style/ContactForm.css'; 
import { useTranslation } from 'react-i18next';

const initialFormData = () => ({
  subject: '',
  message: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  relation: '',
  companyWebsite: '',
});

const ContactForm = ({ sendEmail, options, defaultRelation, defaultMessagePlaceholder }) => {
  const { t } = useTranslation('contact');
  const fallbackRelation = t('contactForm.placeholders.relation');
  const [formData, setFormData] = useState(initialFormData());
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    setErrorMessage('');
    setEmailSent(false);
    setIsSubmitting(true);

    try {
      await sendEmail(formData);
      setEmailSent(true);
      setFormData(initialFormData());
    } catch (error) {
      console.error(t('contactForm.messages.genericError'), error);
      setErrorMessage(error?.message || t('contactForm.messages.genericError'));
      setEmailSent(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {emailSent && <div className="success-message" role="status">{t('contactForm.messages.success')}</div>}
      {errorMessage && <div className="error-message" role="alert">{errorMessage}</div>}
      <form className="contact-form" onSubmit={handleSubmit}>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          required
          minLength={2}
          maxLength={80}
          autoComplete="given-name"
          onChange={handleChange}
          placeholder={t('contactForm.placeholders.firstName')}
        />
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          required
          minLength={2}
          maxLength={80}
          autoComplete="family-name"
          onChange={handleChange}
          placeholder={t('contactForm.placeholders.lastName')}
        />
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          required
          minLength={6}
          maxLength={30}
          autoComplete="tel"
          onChange={handleChange}
          placeholder={t('contactForm.placeholders.phone')}
        />
        <input
          type="email"
          name="email"
          value={formData.email}
          required
          maxLength={254}
          autoComplete="email"
          onChange={handleChange}
          placeholder={t('contactForm.placeholders.email')}
        />
        <select name="subject" value={formData.subject} required onChange={handleChange}>
          <option value="">{t('contactForm.placeholders.subject')}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
        <textarea
          className='textarea'
          name="message" 
          value={formData.message}
          required 
          minLength={20}
          maxLength={3000}
          onChange={handleChange} 
          placeholder={defaultMessagePlaceholder || t('contactForm.placeholders.message')} 
        />
        <div className="contact-form__honeypot" aria-hidden="true">
          <label htmlFor="companyWebsite">{t('contactForm.honeypotLabel')}</label>
          <input
            id="companyWebsite"
            type="text"
            name="companyWebsite"
            value={formData.companyWebsite}
            tabIndex={-1}
            autoComplete="off"
            onChange={handleChange}
          />
        </div>
        <select name="relation" value={formData.relation} required onChange={handleChange}>
          <option value="">{defaultRelation || fallbackRelation}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('contactForm.sendingText') : t('contactForm.buttonText')}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
